'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

// This context hydrates from server-injected details (retrieved via SSR in layout.tsx from Supabase)
export interface CorporateConfig {
    id: string;
    slug: string;
    name: string;
    legal_name?: string;
    hexPrimary: string; // Legacy fallback
    hexSecondary: string; // Legacy fallback
    logoUrl: string | null;
    brand_assets?: {
        logo_light?: string;
        logo_dark?: string;
        favicon?: string;
        support_email?: string;
        [key: string]: any;
    };
    ui_theme?: any; // New Granular UI Theme JSON
    ui_config?: {
        slogan?: string;
        fullSlogan?: string;
        endorsement?: string;
        splashUrl?: string;
        [key: string]: any;
    };
    capabilities: {
        fiatEnabled: boolean;
        cryptoEnabled: boolean;
        posMerchant: boolean;
        multiSigWallets: boolean;
        heroAsset?: string;
        networkName?: string;
        supportedTokens?: string[];
        tokens?: any[];
        defaultToken?: string;
    };
    providers?: {
        fiat?: string | null;
        auth?: string | null;
        accountAbstraction?: string | null;
        legacy_unsalted_derivation?: boolean;
        entryPointAddress?: string;
        contracts?: {
            FTK?: string;
            USDT?: string;
            [key: string]: string | undefined;
        };
        [key: string]: any;
    };
}

interface CorporateContextProps {
    config: CorporateConfig | null;
    corporateId?: string | null;
    corporate?: CorporateConfig | null;
    setConfig: (config: CorporateConfig) => void;
}

const CorporateContext = createContext<CorporateContextProps | undefined>(undefined);

export function CorporateProvider({ children, initialConfig }: { children: React.ReactNode, initialConfig?: CorporateConfig }) {
    const [config, setConfig] = useState<CorporateConfig | null>(initialConfig || null);
    const pathname = usePathname();

    useEffect(() => {
        if (config) {
            const root = document.documentElement;

            // Context-Aware Theme Selection 
            let activeTheme = config.ui_theme?.master || config.ui_theme?.wallet || config.ui_theme?.merchant || config.ui_theme || {};
            if (pathname?.startsWith('/wallet')) {
                activeTheme = config.ui_theme?.wallet || activeTheme;
            } else if (pathname?.startsWith('/merchant')) {
                activeTheme = config.ui_theme?.merchant || activeTheme;
            }

            const baseColors = config.ui_theme?.colors || {};
            const activeColors = activeTheme?.colors || activeTheme || {};

            // Determine tokens explicitly falling back to legacy fields if necessary
            const primary = activeColors.primary || baseColors.primary || config.hexPrimary || '#136dec';
            const primaryHover = activeColors.primaryHover || baseColors.primaryHover || primary;
            const secondary = activeColors.secondary || baseColors.secondary || config.hexSecondary || '#136dec';
            const gradient = activeColors.primaryGradient || `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`;
            const tenantThemeName = config.ui_theme?.branding?.theme || config.ui_theme?.theme || activeTheme?.theme || 'default';

            const bgVal = activeColors.background || baseColors.background || '';
            const isLightMode =
                activeTheme?.mode === 'light' ||
                config.ui_theme?.mode === 'light' ||
                tenantThemeName === 'institutional-light' ||
                bgVal.toLowerCase() === '#ffffff' ||
                bgVal.toLowerCase() === '#f8fafc';

            if (isLightMode) {
                root.classList.remove('dark');
                root.classList.add('light');
                root.setAttribute('data-tenant-mode', 'light');
            } else {
                root.setAttribute('data-tenant-mode', 'dark');
            }
            root.setAttribute('data-tenant-theme', tenantThemeName);

            // Apply Dynamic White-Label Tokens to the root element
            root.style.setProperty('--tenant-primary', primary);
            root.style.setProperty('--tenant-primary-hover', primaryHover);
            root.style.setProperty('--tenant-secondary', secondary);
            root.style.setProperty('--tenant-gradient', gradient);
            root.style.setProperty('--tenant-glow', activeColors.glow || `rgba(18, 104, 196, 0.2)`);
            root.style.setProperty('--tenant-gradient-text', activeColors.textOnGradient || '#ffffff');

            // B2B Advanced Theming Map (Colors)
            root.style.setProperty('--theme-bg', bgVal || (isLightMode ? '#ffffff' : '#f4f7fa'));

            const surfaceVal = activeColors.surface || baseColors.surface || (isLightMode ? '#f3f6fa' : '#0f172a');
            root.style.setProperty('--theme-surface', surfaceVal);

            const cardBgVal = activeColors.cardBg || baseColors.cardBackground || (isLightMode ? '#ffffff' : undefined);
            if (cardBgVal) root.style.setProperty('--theme-card-bg', cardBgVal);
            else root.style.removeProperty('--theme-card-bg');

            const cardDarkVal = activeColors.cardDark || baseColors.surface || (isLightMode ? '#f3f6fa' : undefined);
            if (cardDarkVal) root.style.setProperty('--theme-card-dark', cardDarkVal);
            else root.style.removeProperty('--theme-card-dark');

            const borderVal = activeColors.cardBorder || activeColors.border || baseColors.border || (isLightMode ? '#dce3ec' : undefined);
            if (borderVal) root.style.setProperty('--theme-border', borderVal);
            else root.style.removeProperty('--theme-border');

            const textMainVal = activeColors.textMain || activeColors.text || baseColors.textMain || baseColors.text || (isLightMode ? '#172033' : undefined);
            if (textMainVal) root.style.setProperty('--theme-text-main', textMainVal);
            else root.style.removeProperty('--theme-text-main');

            const textMutedVal = activeColors.textMuted || activeColors.muted || baseColors.textMuted || baseColors.muted || (isLightMode ? '#5b6575' : undefined);
            if (textMutedVal) root.style.setProperty('--theme-text-muted', textMutedVal);
            else root.style.removeProperty('--theme-text-muted');

            const successVal = activeColors.success || baseColors.success || '#168653';
            root.style.setProperty('--theme-success', successVal);

            const errorVal = activeColors.error || baseColors.error || '#C73535';
            root.style.setProperty('--theme-error', errorVal);

            // B2B Advanced Theming Map (Shapes, Layout & Typography)
            const shapeSpec = config.ui_theme?.shape || {};
            const layoutSpec = activeTheme?.layout || config.ui_theme?.layout || {};
            const typoSpec = activeTheme?.typography || config.ui_theme?.typography || {};

            const radiusStandard = activeTheme?.shapes?.borderRadiusStandard || (shapeSpec.borderRadius ? `${shapeSpec.borderRadius}px` : undefined);
            if (radiusStandard) root.style.setProperty('--theme-radius-standard', radiusStandard);
            else root.style.removeProperty('--theme-radius-standard');

            const radiusButton = activeTheme?.shapes?.borderRadiusButton || (shapeSpec.buttonRadius ? `${shapeSpec.buttonRadius}px` : undefined);
            if (radiusButton) root.style.setProperty('--theme-radius-button', radiusButton);
            else root.style.removeProperty('--theme-radius-button');

            const radiusInput = activeTheme?.shapes?.borderRadiusInput || (shapeSpec.inputRadius ? `${shapeSpec.inputRadius}px` : undefined);
            if (radiusInput) root.style.setProperty('--theme-radius-input', radiusInput);
            else root.style.removeProperty('--theme-radius-input');

            if (layoutSpec.buttonHeight) root.style.setProperty('--theme-button-height', `${layoutSpec.buttonHeight}px`);
            if (layoutSpec.inputHeight) root.style.setProperty('--theme-input-height', `${layoutSpec.inputHeight}px`);
            if (layoutSpec.contentMaxWidth) root.style.setProperty('--theme-content-max-width', `${layoutSpec.contentMaxWidth}px`);

            if (typoSpec.fontFamily) root.style.setProperty('--theme-font-family', typoSpec.fontFamily);
            else root.style.removeProperty('--theme-font-family');
        }
    }, [config, pathname]);

    return (
        <CorporateContext.Provider value={{ config, corporateId: config?.id || null, corporate: config, setConfig }}>
            {children}
        </CorporateContext.Provider>
    );
}

export function useCorporate() {
    const context = useContext(CorporateContext);
    if (context === undefined) {
        throw new Error('useCorporate must be used within a CorporateProvider');
    }
    return context;
}
